// ============================================================
// IMEI / TAC decoder — figure out which device a SIM is in
// ============================================================
// An IMEI is 15 digits: TAC (8) + SN (6) + Luhn check digit (1).
// The TAC (Type Allocation Code) is issued by the GSMA to a
// specific brand + model combination — so it's enough to tell us
// "this SIM is sitting in a Quectel BG95 IoT module" without
// needing the manufacturer to phone home.
//
// PRODUCTION: in real-mode we'd POST the TAC to a backend lookup
// against the GSMA Device Database (subscription) or a commercial
// API like OSMOcom IMEI Search / RingCentral TAC API, cache the
// result in Postgres, and fall back to "Unknown" if the TAC isn't
// registered (older devices, grey-market hardware).
//
// DEV: this file ships a static dictionary covering the most-
// common IoT cellular modules + a few enterprise gateways and
// rugged tablets so the demo data looks real. New entries only
// need the first 8 digits of an IMEI.
//
// To add an entry from a real IMEI in the field:
//   1. Take the first 8 digits of the IMEI (the TAC).
//   2. Look it up at https://imeidata.net (free, no API key) to
//      confirm brand/model.
//   3. Add a row below with brand, model, category, and a short
//      "use" string.

const TAC_TABLE = {
  // ─── Sierra Wireless ────────────────────────────────────
  '35209900': { brand: 'Sierra Wireless', model: 'EM7565',     category: 'M.2 modem',     use: 'Embedded modem · routers and gateways (CAT-12)' },
  '35209901': { brand: 'Sierra Wireless', model: 'EM9191',     category: 'M.2 modem',     use: 'Embedded modem · 5G NR sub-6 (CAT-22)' },
  '35209911': { brand: 'Sierra Wireless', model: 'WP7702',     category: 'IoT module',    use: 'Smart wireless · Octave-enabled (CAT-M1)' },
  '35209922': { brand: 'Sierra Wireless', model: 'HL7800',     category: 'IoT module',    use: 'LTE-M / NB-IoT module (CAT-M1)' },

  // ─── Quectel ────────────────────────────────────────────
  '35260907': { brand: 'Quectel',         model: 'EG25-G',     category: 'IoT module',    use: 'Industrial routers + IoT gateways (CAT-4)' },
  '35260914': { brand: 'Quectel',         model: 'BG95-M3',    category: 'IoT module',    use: 'LTE-M / NB-IoT + GNSS (CAT-M1)' },
  '35260920': { brand: 'Quectel',         model: 'RM502Q-AE',  category: 'M.2 modem',     use: '5G sub-6 module · enterprise routers (CAT-22)' },
  '35260930': { brand: 'Quectel',         model: 'EC25-AF',    category: 'IoT module',    use: 'M2M / telematics (CAT-4)' },

  // ─── Telit ──────────────────────────────────────────────
  '86741304': { brand: 'Telit Cinterion', model: 'LM940',      category: 'M.2 modem',     use: 'Embedded WAN — NAB-rated routers (CAT-11)' },
  '86741310': { brand: 'Telit Cinterion', model: 'ME910C1-WW', category: 'IoT module',    use: 'LTE-M / NB-IoT (CAT-M1)' },

  // ─── u-blox ────────────────────────────────────────────
  '35309210': { brand: 'u-blox',          model: 'SARA-R5',    category: 'IoT module',    use: 'LTE-M / NB-IoT with secure-element (CAT-M1)' },

  // ─── Nordic Semiconductor ───────────────────────────────
  '35471510': { brand: 'Nordic',          model: 'nRF9160',    category: 'IoT SiP',       use: 'System-in-package · asset tracking (CAT-M1)' },

  // ─── Cradlepoint / Peplink enterprise gateways ──────────
  '35888044': { brand: 'Cradlepoint',     model: 'IBR900',     category: 'Vehicle router',use: 'In-vehicle dual-modem router (CAT-18)' },
  '35888045': { brand: 'Cradlepoint',     model: 'E300',       category: 'Branch router', use: '5G branch router (CAT-22)' },
  '35888046': { brand: 'Cradlepoint',     model: 'W1850',      category: 'Adapter',       use: 'Wireless adapter (CAT-20)' },
  '35666778': { brand: 'Peplink',         model: 'MAX BR1 Pro 5G', category: 'Vehicle router', use: 'Dual-modem 5G vehicle router' },
  '35666779': { brand: 'Peplink',         model: 'MAX Transit Pro', category: 'Vehicle router', use: 'Quad-modem mobile router' },

  // ─── Inseego enterprise gateways ────────────────────────
  '35944210': { brand: 'Inseego',         model: 'FX2000e',    category: 'Branch router', use: '5G indoor router · failover' },
  '35944211': { brand: 'Inseego',         model: 'M2000',      category: 'Mobile hotspot',use: '5G mobile hotspot (mobile broadband)' },

  // ─── Honeywell / Zebra rugged ───────────────────────────
  '35710033': { brand: 'Honeywell',       model: 'CT60 XP',    category: 'Rugged handheld', use: 'Warehouse / DC scanning' },
  '35710044': { brand: 'Zebra',           model: 'TC72',       category: 'Rugged handheld', use: 'Field service · barcode scanning' },

  // ─── Samsung Galaxy Tab Active (rugged) ─────────────────
  '35415521': { brand: 'Samsung',         model: 'Galaxy Tab Active4 Pro', category: 'Rugged tablet', use: 'Field worker tablet (Verizon/AT&T)' },

  // ─── Apple iPad cellular (common in retail / kiosks) ────
  '35234511': { brand: 'Apple',           model: 'iPad (10th gen) Cellular', category: 'Tablet', use: 'Retail POS · kiosk' },
};

/**
 * decodeImei — parse an IMEI string and return the parts + a
 * best-effort device identification.
 *
 * Returns:
 *   { tac, sn, check, valid, device, source }
 *
 * device may be null if the TAC isn't in our dictionary; the UI
 * should show "Unknown device" + offer to submit the IMEI to ops
 * for a real GSMA database lookup.
 */
export function decodeImei(imei) {
  if (!imei || typeof imei !== 'string') {
    return { tac: null, sn: null, check: null, valid: false, device: null, source: null };
  }
  const digits = imei.replace(/\D/g, '');
  if (digits.length < 14) {
    return { tac: digits, sn: null, check: null, valid: false, device: null, source: null };
  }
  const tac   = digits.slice(0, 8);
  const sn    = digits.slice(8, 14);
  const check = digits.length >= 15 ? digits.slice(14, 15) : null;
  const valid = digits.length === 15 && (check === null || luhnChecksum(digits.slice(0, 14)) === Number(check));

  const device = TAC_TABLE[tac] || null;
  return {
    tac,
    sn,
    check,
    valid,
    device,
    // Where the lookup came from — useful for the "How we know"
    // tooltip in the UI ("Looked up in our cached GSMA TAC mirror"
    // vs "Resolved live via GSMA Device Database").
    source: device ? 'cached-tac-dictionary' : null,
  };
}

/**
 * Luhn-mod-10 checksum validator. Standard for IMEI.
 * Returns the expected check digit for the given 14-digit prefix.
 * Exported as the single source of truth (mock data reuses it).
 */
export function luhnChecksum(prefix14) {
  let sum = 0;
  for (let i = 13; i >= 0; i--) {
    let d = Number(prefix14[i]);
    if ((13 - i) % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return (10 - (sum % 10)) % 10;
}
